"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

interface ContentItem {
  id: string;
  quantity: number;
  item_price?: number;
}

export type EventData = {
  content_category?: string;
  content_ids?: string[];
  content_name?: string;
  content_type?: string;
  contents?: ContentItem[];
  currency?: string;
  num_items?: number;
  predicted_ltv?: number;
  search_string?: string;
  status?: boolean;
  value?: number;
} & Record<string, unknown>;

export interface EventOptions {
  eventID?: string;
}

type PendingEvent =
  | { method: "track"; args: [string, EventData | undefined, EventOptions | undefined] }
  | { method: "trackCustom"; args: [string, EventData | undefined, EventOptions | undefined] }
  | { method: "pageView"; args: [] };

type ReadySubscriber = (isReady: boolean) => void;

let pixelModule: any = null;
let initializationPromise: Promise<void> | null = null;
let isPixelReady = false;
let activePixelId: string | null = null;
let consentOverride: boolean | null = null;
const pendingEvents: PendingEvent[] = [];
const readySubscribers = new Set<ReadySubscriber>();

const HAS_ACCEPTED_COOKIES_KEY = "hasAcceptedCookies";

const parseConsentValue = (value: string | null | undefined) => {
  if (!value) {
    return false;
  }

  const normalized = value.toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "granted";
};

const getCookie = (name: string) => {
  if (typeof document === "undefined") {
    return null;
  }

  try {
    const cookieString = document.cookie;
    if (!cookieString) return null;

    const prefix = `${encodeURIComponent(name)}=`;
    const cookies = cookieString.split(";");
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.slice(prefix.length));
      }
    }
  } catch (_) {
    return null;
  }

  return null;
};

const getLocalStorageItem = (key: string) => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage.getItem(key);
  } catch (_) {
    return null;
  }
};

const hasConsent = () => {
  if (typeof consentOverride === "boolean") {
    return consentOverride;
  }

  const cookieValue = getCookie(HAS_ACCEPTED_COOKIES_KEY);
  if (cookieValue !== null) {
    return parseConsentValue(cookieValue);
  }

  const localStorageValue = getLocalStorageItem(HAS_ACCEPTED_COOKIES_KEY);
  if (localStorageValue !== null) {
    return parseConsentValue(localStorageValue);
  }

  return false;
};

const notifyReadySubscribers = (ready: boolean) => {
  readySubscribers.forEach((subscriber) => {
    try {
      subscriber(ready);
    } catch (error) {
      console.error("Facebook Pixel subscriber failed", error);
    }
  });
};

const flushPendingEvents = () => {
  if (!pixelModule) {
    return;
  }

  while (pendingEvents.length) {
    const event = pendingEvents.shift();
    if (!event) continue;

    if (event.method === "track") {
      pixelModule.track(...event.args);
    } else if (event.method === "trackCustom") {
      pixelModule.trackCustom(...event.args);
    } else {
      pixelModule.pageView();
    }
  }
};

const resetPixelState = () => {
  isPixelReady = false;
  pixelModule = null;
  initializationPromise = null;
  pendingEvents.length = 0;
  notifyReadySubscribers(false);
};

const ensurePixelInitialized = () => {
  if (
    isPixelReady ||
    initializationPromise ||
    !activePixelId ||
    typeof window === "undefined" ||
    !hasConsent()
  ) {
    return initializationPromise ?? Promise.resolve();
  }

  initializationPromise = import("react-facebook-pixel")
    .then((module) => {
      pixelModule = module.default;
      pixelModule.init(activePixelId, {
        autoConfig: true,
        debug: false,
      });
      isPixelReady = true;
      notifyReadySubscribers(true);
      flushPendingEvents();
    })
    .catch((error) => {
      console.error("Failed to initialize Facebook Pixel", error);
      initializationPromise = null;
      throw error;
    });

  return initializationPromise;
};

const enqueueOrDispatchEvent = (event: PendingEvent) => {
  if (!activePixelId || typeof window === "undefined" || !hasConsent()) {
    return;
  }

  if (isPixelReady && pixelModule) {
    if (event.method === "track") {
      pixelModule.track(...event.args);
    } else if (event.method === "trackCustom") {
      pixelModule.trackCustom(...event.args);
    } else {
      pixelModule.pageView();
    }
    return;
  }

  pendingEvents.push(event);
  void ensurePixelInitialized();
};

const facebookPixelService = {
  isReady: () => isPixelReady,
  configure: ({
    pixelId,
    consentGranted,
  }: {
    pixelId?: string | null;
    consentGranted?: boolean;
  }) => {
    const normalizedPixelId = pixelId?.trim() || null;
    const nextConsent =
      typeof consentGranted === "boolean" ? consentGranted : consentOverride;
    const pixelIdChanged = normalizedPixelId !== activePixelId;
    const consentChanged = nextConsent !== consentOverride;

    if (pixelIdChanged || consentChanged) {
      activePixelId = normalizedPixelId;
      consentOverride = nextConsent ?? null;
      if (!activePixelId || !hasConsent() || pixelIdChanged) {
        resetPixelState();
      }
    }
  },
  ensureInitialized: ensurePixelInitialized,
  subscribe: (subscriber: ReadySubscriber) => {
    readySubscribers.add(subscriber);
    return () => {
      readySubscribers.delete(subscriber);
    };
  },
  track: (eventName: string, data?: EventData, options?: EventOptions) =>
    enqueueOrDispatchEvent({ method: "track", args: [eventName, data, options] }),
  trackCustom: (eventName: string, data?: EventData, options?: EventOptions) =>
    enqueueOrDispatchEvent({ method: "trackCustom", args: [eventName, data, options] }),
  pageView: () => enqueueOrDispatchEvent({ method: "pageView", args: [] }),
};

const FBP_COOKIE = "_fbp";
const FBC_COOKIE = "_fbc";
const FBC_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

// Meta's pixel writes `_fbc` from `fbclid` only after it has loaded. Persist it
// ourselves on landing so the click ID survives a fast checkout or a slow pixel.
const persistFbcFromUrl = () => {
  if (typeof window === "undefined" || !hasConsent()) return;

  const fbclid = new URLSearchParams(window.location.search).get("fbclid");
  if (!fbclid) return;
  if (getCookie(FBC_COOKIE)?.endsWith(`.${fbclid}`)) return;

  const value = encodeURIComponent(`fb.1.${Date.now()}.${fbclid}`);
  document.cookie = `${FBC_COOKIE}=${value}; Max-Age=${FBC_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
};

export type MetaBrowserIds = { fbp?: string; fbc?: string };

/**
 * Browser identifiers Meta uses to match a server-side event to an ad click.
 * Returns null when the studio has no pixel or the visitor hasn't consented.
 */
export const getMetaBrowserIds = (): MetaBrowserIds | null => {
  if (!activePixelId || !hasConsent()) return null;

  persistFbcFromUrl();
  return {
    fbp: getCookie(FBP_COOKIE) ?? undefined,
    fbc: getCookie(FBC_COOKIE) ?? undefined,
  };
};

export const generateEventId = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (error) {
    // noop – fall back to timestamp-based identifier
  }

  return `fb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// Hook API aligned with apps/website/utils/facebook-pixel.ts
export const useFacebookPixel = () => {
  const [isReady, setIsReady] = useState(facebookPixelService.isReady());

  useEffect(() => {
    const unsubscribe = facebookPixelService.subscribe(setIsReady);
    void facebookPixelService.ensureInitialized();
    return unsubscribe;
  }, []);

  return useMemo(
    () => ({
      isReady,
      AddPaymentInfo: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("AddPaymentInfo", data, options),
      AddToCart: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("AddToCart", data, options),
      CompleteRegistration: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("CompleteRegistration", data, options),
      Contact: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("Contact", data, options),
      CustomizeProduct: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("CustomizeProduct", data, options),
      InitiateCheckout: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("InitiateCheckout", data, options),
      Lead: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("Lead", data, options),
      Schedule: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("Schedule", data, options),
      Search: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("Search", data, options),
      StartTrial: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("StartTrial", data, options),
      SubmitApplication: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("SubmitApplication", data, options),
      Subscribe: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("Subscribe", data, options),
      ViewContent: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("ViewContent", data, options),
      Purchase: (data?: EventData, options?: EventOptions) =>
        facebookPixelService.track("Purchase", data, options),
      Custom: (event: string, data?: EventData, options?: EventOptions) =>
        facebookPixelService.trackCustom(event, data, options),
    }),
    [isReady],
  );
};

export const FacebookPixel = ({
  pixelId,
  consentGranted,
}: {
  pixelId: string;
  consentGranted?: boolean;
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams?.toString();
  const previousConsent = useRef(consentGranted);

  useEffect(() => {
    const wasGranted = previousConsent.current;
    previousConsent.current = consentGranted;

    facebookPixelService.configure({ pixelId, consentGranted });
    persistFbcFromUrl();
    void facebookPixelService.ensureInitialized().then(() => {
      if (consentGranted && !wasGranted) {
        facebookPixelService.pageView();
      }
    });
  }, [pixelId, consentGranted]);

  useEffect(() => {
    if (!pathname) return;
    persistFbcFromUrl();
    facebookPixelService.pageView();
  }, [pathname, search]);

  return null;
};
